import { useMemo, useState, type ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import { Check, Copy, RefreshCw, Search, WandSparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OptValues } from "@/lib/utilfns";
import { Card } from "@/components/ui/primitives";

/* ── Utility definition types (the catalog in src/data/utilities.tsx builds
      on these; keep this module free of data imports to avoid cycles) ─────── */

export interface UtilityOption {
  key: string;
  label: string;
  type: "select" | "number" | "checkbox" | "text";
  choices?: string[];
  default: string | number | boolean;
  min?: number;
  max?: number;
  placeholder?: string;
}

export interface UtilityField {
  key: string;
  label: string;
  type: "text" | "number" | "select" | "color" | "date" | "datetime-local" | "textarea";
  choices?: string[];
  default?: string;
  placeholder?: string;
}

export type UtilityEngine =
  | {
      kind: "transform";
      fn: (input: string, opts: OptValues) => string;
      placeholder?: string;
      sample?: string;
      options?: UtilityOption[];
    }
  | {
      kind: "generate";
      fn: (opts: OptValues) => string;
      options?: UtilityOption[];
      button?: string;
    }
  | { kind: "reference"; columns: string[]; rows: string[][]; note?: string }
  | {
      kind: "fields";
      fields: UtilityField[];
      compute: (values: Record<string, string>) => { label: string; value: string }[] | string;
      note?: string;
    }
  | { kind: "custom"; component: ComponentType };

export interface UtilityDef {
  id: string;
  name: string;
  tagline: string;
  category: string;
  icon: LucideIcon;
  keywords?: string;
  engine: UtilityEngine;
}

/* ── Shared bits ──────────────────────────────────────────────────────────── */

export const inputCls =
  "w-full rounded-lg border border-app bg-surface-2 px-3 py-2 text-xs text-app outline-none placeholder:text-app-2 focus:border-app-strong";
export const textareaCls = cn(inputCls, "min-h-[120px] resize-y font-mono leading-relaxed");
export const labelCls = "mb-1 block text-[11px] font-medium uppercase tracking-wider text-app-2";

export function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      disabled={!text}
      className={cn(
        "flex items-center gap-1.5 rounded-lg border border-app bg-surface-2 px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong disabled:opacity-40",
        className
      )}
    >
      {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function OutputPane({
  text,
  error,
  minHeight = 120,
}: {
  text: string;
  error?: string | null;
  minHeight?: number;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className={cn(labelCls, "mb-0")}>Output</span>
        <CopyButton text={error ? "" : text} />
      </div>
      <pre
        className={cn(
          "w-full overflow-auto whitespace-pre-wrap break-words rounded-lg border border-app bg-surface-2 px-3 py-2 font-mono text-xs leading-relaxed",
          error ? "text-rose-400" : "text-app"
        )}
        style={{ minHeight }}
      >
        {error ?? (text || " ")}
      </pre>
    </div>
  );
}

function OptionControls({
  options,
  values,
  onChange,
}: {
  options: UtilityOption[];
  values: OptValues;
  onChange: (key: string, v: string | number | boolean) => void;
}) {
  if (!options.length) return null;
  return (
    <div className="flex flex-wrap items-end gap-3">
      {options.map((o) => (
        <label key={o.key} className="block">
          <span className={labelCls}>{o.label}</span>
          {o.type === "select" ? (
            <select
              value={String(values[o.key])}
              onChange={(e) => onChange(o.key, e.target.value)}
              className={cn(inputCls, "w-auto pr-7")}
            >
              {o.choices?.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : o.type === "number" ? (
            <input
              type="number"
              value={Number(values[o.key])}
              min={o.min}
              max={o.max}
              onChange={(e) => onChange(o.key, Number(e.target.value))}
              className={cn(inputCls, "w-24")}
            />
          ) : o.type === "checkbox" ? (
            <span className="flex h-[30px] items-center">
              <input
                type="checkbox"
                checked={Boolean(values[o.key])}
                onChange={(e) => onChange(o.key, e.target.checked)}
                className="h-4 w-4 accent-amber-500"
              />
            </span>
          ) : (
            <input
              value={String(values[o.key])}
              placeholder={o.placeholder}
              onChange={(e) => onChange(o.key, e.target.value)}
              className={cn(inputCls, "w-40")}
            />
          )}
        </label>
      ))}
    </div>
  );
}

function useOptions(options?: UtilityOption[]) {
  const [values, setValues] = useState<OptValues>(() =>
    Object.fromEntries((options ?? []).map((o) => [o.key, o.default]))
  );
  return {
    values,
    set: (key: string, v: string | number | boolean) =>
      setValues((prev) => ({ ...prev, [key]: v })),
  };
}

/* ── Engines ──────────────────────────────────────────────────────────────── */

function TransformTool({ engine }: { engine: Extract<UtilityEngine, { kind: "transform" }> }) {
  const [input, setInput] = useState("");
  const { values, set } = useOptions(engine.options);
  const result = useMemo(() => {
    if (!input) return { text: "", error: null as string | null };
    try {
      return { text: engine.fn(input, values), error: null };
    } catch (e) {
      return { text: "", error: e instanceof Error ? e.message : String(e) };
    }
  }, [input, values, engine]);

  return (
    <div className="space-y-4">
      {engine.options && (
        <OptionControls options={engine.options} values={values} onChange={set} />
      )}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className={cn(labelCls, "mb-0")}>Input</span>
          {engine.sample && (
            <button
              onClick={() => setInput(engine.sample!)}
              className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2 px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <WandSparkles size={12} /> Load sample
            </button>
          )}
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={engine.placeholder ?? "Paste or type input…"}
          className={textareaCls}
          spellCheck={false}
        />
      </div>
      <OutputPane text={result.text} error={result.error} />
    </div>
  );
}

function GeneratorTool({ engine }: { engine: Extract<UtilityEngine, { kind: "generate" }> }) {
  const { values, set } = useOptions(engine.options);
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const run = () => {
    try {
      setOutput(engine.fn(values));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {engine.options && (
          <OptionControls options={engine.options} values={values} onChange={set} />
        )}
        <button
          onClick={run}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
        >
          <RefreshCw size={13} /> {engine.button ?? "Generate"}
        </button>
      </div>
      <OutputPane text={output} error={error} minHeight={160} />
    </div>
  );
}

function ReferenceTool({ engine }: { engine: Extract<UtilityEngine, { kind: "reference" }> }) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return engine.rows;
    return engine.rows.filter((r) => r.some((c) => c.toLowerCase().includes(needle)));
  }, [q, engine.rows]);

  const isSwatch = (cell: string) => /^#[0-9a-f]{6}$/i.test(cell);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2 px-2.5 py-1.5">
        <Search size={13} className="text-app-2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Filter ${engine.rows.length} entries…`}
          className="w-full bg-transparent text-xs text-app outline-none placeholder:text-app-2"
        />
      </div>
      {engine.note && <p className="text-[11px] leading-relaxed text-app-2">{engine.note}</p>}
      <Card className="overflow-hidden">
        <div className="max-h-[440px] overflow-auto">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-surface backdrop-blur-xl">
              <tr>
                {engine.columns.map((c) => (
                  <th
                    key={c}
                    className="border-b border-app px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-app-2"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-app/40 last:border-0 hover:bg-surface-2/60">
                  {r.map((cell, j) => (
                    <td key={j} className={cn("px-3 py-1.5 align-top text-app", j === 0 && "font-mono font-medium")}>
                      {isSwatch(cell) ? (
                        <span className="flex items-center gap-2 font-mono">
                          <span
                            className="inline-block h-3.5 w-3.5 rounded border border-app"
                            style={{ background: cell }}
                          />
                          {cell}
                        </span>
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={engine.columns.length} className="px-3 py-6 text-center text-app-2">
                    No matches.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FieldsTool({ engine }: { engine: Extract<UtilityEngine, { kind: "fields" }> }) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(engine.fields.map((f) => [f.key, f.default ?? ""]))
  );
  const result = useMemo(() => {
    try {
      return { out: engine.compute(values), error: null as string | null };
    } catch (e) {
      return { out: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [values, engine]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {engine.fields.map((f) => (
          <label key={f.key} className={cn("block", f.type === "textarea" && "col-span-2")}>
            <span className={labelCls}>{f.label}</span>
            {f.type === "select" ? (
              <select
                value={values[f.key]}
                onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                className={inputCls}
              >
                {f.choices?.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                className={textareaCls}
                spellCheck={false}
              />
            ) : (
              <input
                type={f.type}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))}
                className={cn(inputCls, f.type === "color" && "h-[34px] cursor-pointer p-1")}
              />
            )}
          </label>
        ))}
      </div>
      {engine.note && <p className="text-[11px] leading-relaxed text-app-2">{engine.note}</p>}
      {result.error ? (
        <Card className="p-3">
          <div className="font-mono text-xs text-rose-400">{result.error}</div>
        </Card>
      ) : typeof result.out === "string" ? (
        <OutputPane text={result.out} />
      ) : result.out ? (
        <Card className="divide-y divide-app/40">
          {result.out.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <span className="text-[11px] uppercase tracking-wider text-app-2">{row.label}</span>
              <span className="select-all text-right font-mono text-xs font-medium text-app">
                {row.value}
              </span>
            </div>
          ))}
        </Card>
      ) : null}
    </div>
  );
}

/* ── Dispatcher ───────────────────────────────────────────────────────────── */

export function UtilityView({ def }: { def: UtilityDef }) {
  const e = def.engine;
  // Keyed by utility id so switching tools never carries state across.
  switch (e.kind) {
    case "transform":
      return <TransformTool key={def.id} engine={e} />;
    case "generate":
      return <GeneratorTool key={def.id} engine={e} />;
    case "reference":
      return <ReferenceTool key={def.id} engine={e} />;
    case "fields":
      return <FieldsTool key={def.id} engine={e} />;
    case "custom": {
      const C = e.component;
      return <C key={def.id} />;
    }
  }
}
